import { useMutation } from '@apollo/client';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

import Button from '@/componentsv2/Button';
import DatePicker from '@/componentsv2/DatePicker';
import Input from '@/componentsv2/Input';
import { firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { toDateOnly } from '../dates';
import { HOURS_PATTERN } from '../money';
import { SUBMIT_ENGAGEMENT_HOURS } from '../operations';

/** What a submission split at NTE-remaining didn't fit. `hours` is null when the api's figure is unusable. */
export interface Surplus {
  hours: number | null;
}

interface SubmitHoursFormProps {
  engagementId: string;
  /** The period the current-period row names. Sent as is, in place of the period fields. */
  period?: { start: string; end: string };
  onClose: () => void;
  /** Null when a submission starts, then the surplus if one didn't fit. */
  onSurplusChange: (surplus: Surplus | null) => void;
}

const UNTOUCHED = { hours: false, description: false };

/**
 * The expert's hours submission, opened from the current-period row that the
 * server's render-only `not_yet_submitted` row becomes.
 *
 * `submitEngagementHours` splits at NTE-remaining rather than rejecting, so
 * `amendmentRequired: true` is a **success** carrying an accepted row. The form
 * reports what didn't fit through `onSurplusChange`, and the row shows it; only
 * a submission with nothing left to fit fails, as `engagement.nte_exceeded`.
 *
 * The period is optional and presentational by api contract, but it is what
 * tells the work approver which week they are confirming — optional as a pair,
 * since the ledger renders a range or nothing at all. When the row names the
 * period, the form sends that and asks for none.
 *
 * Cancel closes the form at any point short of a submission in flight, and the
 * draft and any rejection of it go with it; it never reaches the api. A
 * submission that fits closes the form too. One with a surplus keeps it open,
 * cleared for the rest. Each field's error waits until that field is edited or
 * a submit is attempted, so starting one field never flags the others.
 */
export default function SubmitHoursForm({
  engagementId,
  period,
  onClose,
  onSurplusChange,
}: SubmitHoursFormProps): JSX.Element {
  const [hours, setHours] = useState('');
  const [workDescription, setWorkDescription] = useState('');
  const [periodStart, setPeriodStart] = useState<string | null>(null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [touched, setTouched] = useState(UNTOUCHED);
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState(0);
  const hoursRef = useRef<HTMLInputElement>(null);
  const [submitHours, { loading }] = useMutation(SUBMIT_ENGAGEMENT_HOURS, {
    // The synthetic current-period row, `nteHoursConsumed` and `hoursPending`
    // are all server-computed off the whole ledger, so the payload alone cannot
    // merge them into the cache.
    refetchQueries: ['engagementHub'],
  });

  // The form is opened on purpose, so it takes focus.
  useEffect(() => {
    hoursRef.current?.focus();
  }, []);

  const trimmedDescription = workDescription.trim();
  const hoursError = !HOURS_PATTERN.test(hours.trim()) || Number.parseFloat(hours) <= 0;
  const descriptionError = trimmedDescription.length === 0;
  // Optional as a *pair*: `HoursRow` renders a range or a dash, so a lone end
  // would persist on the record and never be seen by the approver.
  const periodIncomplete = (periodStart === null) !== (periodEnd === null);
  const periodInverted =
    periodStart !== null && periodEnd !== null && moment(periodEnd).isBefore(moment(periodStart));
  const datesError = periodIncomplete || periodInverted;
  const sentStart = period?.start ?? periodStart;
  const sentEnd = period?.end ?? periodEnd;
  const canSubmit = !hoursError && !descriptionError && !datesError && !loading;
  const showHoursError = (touched.hours || attempted) && hoursError;
  const showDescriptionError = (touched.description || attempted) && descriptionError;

  function clearDraft() {
    setHours('');
    setWorkDescription('');
    setPeriodStart(null);
    setPeriodEnd(null);
    setTouched(UNTOUCHED);
    setAttempted(false);
    // A half-typed date reaches us as null, so resetting to null changes no
    // prop and the pickers keep showing it. Remounting them clears it.
    setDraftKey((key) => key + 1);
  }

  async function handleSubmit() {
    setAttempted(true);
    if (!canSubmit) return;
    setError(null);
    onSurplusChange(null);
    try {
      const { data } = await submitHours({
        variables: {
          input: {
            engagementId,
            hours: Number.parseFloat(hours).toFixed(2),
            workDescription: trimmedDescription,
            // Omitted rather than sent as null, so an unset period stays unset.
            ...(sentStart ? { periodStart: sentStart } : {}),
            ...(sentEnd ? { periodEnd: sentEnd } : {}),
          },
        },
      });
      const payload = data?.submitEngagementHours;
      // `amendmentRequired` is the authority, not the figure: a surplus that
      // doesn't parse costs the banner its number, never the banner itself.
      const parsed = Number.parseFloat(payload?.surplusHours ?? '');
      onSurplusChange(payload?.amendmentRequired ? { hours: parsed > 0 ? parsed : null } : null);
      clearDraft();
      if (!payload?.amendmentRequired) onClose();
    } catch (err) {
      setError(
        getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? 'These hours could not be submitted.'
      );
    }
  }

  return (
    <div className="flex flex-col gap-16 rounded-md border border-surface-border bg-surface-elevated px-16 py-16">
      <span className="text-on-surface subtitle-2">Submit hours</span>

      <Input
        label="Hours worked"
        inputRef={hoursRef}
        value={hours}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setTouched((prev) => ({ ...prev, hours: true }));
          setHours(e.target.value);
        }}
        error={showHoursError}
        helperText={
          showHoursError ? 'Enter a positive number with at most two decimal places.' : ''
        }
      />

      <Input
        label="What you worked on"
        multiline
        rows={3}
        value={workDescription}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setTouched((prev) => ({ ...prev, description: true }));
          setWorkDescription(e.target.value);
        }}
        error={showDescriptionError}
        helperText={
          showDescriptionError
            ? 'Describe the work these hours cover.'
            : 'The work approver sees this when they confirm.'
        }
      />

      {period ? null : (
        <fieldset key={draftKey} className="flex flex-col gap-8 border-[0] p-[0]">
          <legend className="text-on-surface-muted subtitle-2">Period covered (optional)</legend>
          <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
            <DatePicker
              label="From"
              value={periodStart ? moment(periodStart) : null}
              onChange={(v) => setPeriodStart(asDateOnly(v))}
            />
            <DatePicker
              label="To"
              value={periodEnd ? moment(periodEnd) : null}
              onChange={(v) => setPeriodEnd(asDateOnly(v))}
            />
          </div>
          {attempted && datesError ? (
            <p className="text-red-600 body-2">
              {periodIncomplete
                ? 'Set both ends of the period, or leave both empty.'
                : 'The period end cannot be before the start.'}
            </p>
          ) : null}
        </fieldset>
      )}

      {error ? <p className="text-red-600 body-2">{error}</p> : null}

      <div className="flex justify-end gap-8">
        <Button variant="tertiary" size="small" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button size="small" onClick={() => void handleSubmit()} disabled={loading}>
          Submit hours
        </Button>
      </div>
    </div>
  );
}

/**
 * The picker hands back a moment (or a current-checkbox shape we don't use).
 * The period is a calendar date on the wire, so send the picked day itself.
 */
function asDateOnly(value: unknown): string | null {
  return moment.isMoment(value) ? toDateOnly(value) : null;
}
